import { eventConfig, isGiveawayOpen, isGiveawayUpcoming } from "./config/event";
import { quizConfig } from "./config/quiz";
import { escapeHtml, iconArrow, iconDownload, iconShare } from "./dom";
import { submitEntry } from "./engine/api";
import { calculateQuizResult, hasCompleteQuiz, type QuizScore } from "./engine/scoring";
import { copyReferralLink, downloadShareCard, shareResult } from "./engine/share";
import { normalizeEmail, validateEntryForm, type EntryFormErrors } from "./engine/validation";
import type {
  EntryFormValues,
  EntryMethod,
  QuizAnswers,
  SubmitEntryPayload,
  SubmitEntryResponse,
} from "./types";

type Screen = "landing" | "quiz" | "entry" | "result" | "confirmation";

const initialForm: EntryFormValues = {
  firstName: "",
  email: "",
  ageAndResidenceConfirmed: false,
  website: "",
};

export class TerpeneQuizApp {
  private readonly root: HTMLElement;
  private readonly params = new URLSearchParams(window.location.search);
  private screen: Screen = "landing";
  private entryMethod: EntryMethod = "quiz";
  private questionIndex = 0;
  private answers: QuizAnswers = {};
  private form: EntryFormValues = { ...initialForm };
  private formErrors: EntryFormErrors = {};
  private score: QuizScore | null = null;
  private submission: SubmitEntryResponse | null = null;
  private isSubmitting = false;
  private notice = "";

  constructor(root: HTMLElement) {
    this.root = root;
    const mode = this.params.get("mode");
    if (mode === "giveaway") {
      this.screen = "entry";
      this.entryMethod = "giveaway";
    } else if (mode === "quiz" || mode === "standalone" || this.params.get("quiz") === "1") {
      this.screen = "quiz";
    }
  }

  mount(): void {
    this.render();
  }

  private get previewMode(): boolean {
    return import.meta.env.DEV && this.params.get("preview") === "1";
  }

  private get standaloneMode(): boolean {
    return this.params.get("mode") === "standalone";
  }

  private logo(): string {
    return `
      <a class="brand" href="${eventConfig.landingUrl}" target="_top" aria-label="Root to Rise Terpenes home">
        <img src="/assets/root-to-rise-logo-cream.png" alt="Root to Rise" width="300" height="275" />
      </a>`;
  }

  private closeButton(): string {
    if (this.params.get("embedded") !== "1") return "";
    return `<button type="button" class="close-button" data-action="close" aria-label="Close quiz"><span aria-hidden="true"></span><span aria-hidden="true"></span></button>`;
  }

  private render(): void {
    const body = this.screen === "landing"
      ? this.renderLanding()
      : this.screen === "quiz"
        ? this.renderQuiz()
        : this.screen === "entry"
          ? this.renderEntry()
          : this.screen === "confirmation"
            ? this.renderConfirmation()
            : this.renderResult();

    this.root.innerHTML = `<main class="app-shell">${this.closeButton()}${body}<div class="sr-only" aria-live="polite">${escapeHtml(this.notice)}</div></main>`;
    this.bindEvents();
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  private renderLanding(): string {
    const eventState = isGiveawayUpcoming() ? "Giveaway opens 30 August" : isGiveawayOpen() ? "Giveaway open now" : "Quiz still open";
    return `
      <section class="landing screen-enter">
        <div class="landing__content">
          ${this.logo()}
          <div class="landing__copy">
            <h1>Which<br />terpene<br />are you?</h1>
            <p>${quizConfig.questions.length} quick questions to discover the aromatic personality that fits your current rhythm.</p>
          </div>
          <div class="landing__actions">
            <button type="button" class="button button--primary" data-action="start-quiz">
              <span>Take the 45-second quiz</span>${iconArrow()}
            </button>
            <button type="button" class="button button--secondary" data-action="start-giveaway">
              <span>Enter the giveaway only</span>${iconArrow()}
            </button>
          </div>
          <p class="event-line"><span>${escapeHtml(eventConfig.name)}</span> · ${escapeHtml(eventConfig.location.replace(", Victoria", ""))} · ${escapeHtml(eventConfig.eventDateLabel)}</p>
          <p class="event-state">${eventState}</p>
        </div>
        <img class="landing__image" src="/assets/hero-botanicals.webp" alt="Lavender, citrus peel, mango, hops, peppercorns and cloves" width="1120" height="1400" fetchpriority="high" />
      </section>`;
  }

  private renderQuiz(): string {
    const question = quizConfig.questions[this.questionIndex];
    if (!question) return "";
    const currentAnswer = this.answers[question.id];
    const progress = ((this.questionIndex + 1) / quizConfig.questions.length) * 100;
    return `
      <section class="quiz-screen screen-enter">
        <header class="quiz-header">
          ${this.logo()}
          <p class="quiz-count">${this.questionIndex + 1} of ${quizConfig.questions.length}</p>
          <div class="progress" role="progressbar" aria-label="Quiz progress" aria-valuemin="1" aria-valuemax="${quizConfig.questions.length}" aria-valuenow="${this.questionIndex + 1}">
            <span style="width:${progress}%"></span>
          </div>
        </header>
        <div class="quiz-body">
          <h1>${escapeHtml(question.prompt)}</h1>
          <div class="answer-list" role="radiogroup" aria-label="${escapeHtml(question.prompt)}">
            ${question.answers.map((answer) => `
              <button type="button" class="answer${currentAnswer === answer.id ? " is-selected" : ""}" data-answer="${answer.id}" role="radio" aria-checked="${currentAnswer === answer.id}">
                ${escapeHtml(answer.label)}
              </button>`).join("")}
          </div>
          <button type="button" class="back-button" data-action="back" ${this.questionIndex === 0 ? "hidden" : ""}>${iconArrow("left")}<span>Back</span></button>
        </div>
        <div class="quiz-botanical" aria-hidden="true"></div>
      </section>`;
  }

  private renderEntry(): string {
    const quizEntry = this.entryMethod === "quiz";
    const upcoming = isGiveawayUpcoming() && !this.previewMode;
    const closed = !isGiveawayOpen() && !isGiveawayUpcoming() && !this.previewMode;
    const heading = quizEntry ? "Reveal my terpene" : "Enter the giveaway";
    const intro = quizEntry
      ? "Enter the draw and we’ll email your result, festival offer and personal sharing link."
      : "Enter the draw without completing the quiz. We’ll email your confirmation and an invitation to discover your terpene later.";

    return `
      <section class="entry-screen screen-enter">
        <div class="entry-decoration" aria-hidden="true"></div>
        <div class="entry-inner">
          ${this.logo()}
          <h1>${heading}</h1>
          <p class="entry-intro">${intro}</p>
          ${upcoming ? `<div class="form-message">Entries open on 30 August 2026. Add <strong>?preview=1</strong> locally to test the form.</div>` : ""}
          ${closed ? `<div class="form-message">${escapeHtml(eventConfig.closedContent.heading)}. ${escapeHtml(eventConfig.closedContent.body)}</div>` : ""}
          <form class="entry-form" novalidate>
            ${this.inputField("firstName", "First name", "text", "given-name")}
            ${this.inputField("email", "Email address", "email", "email")}
            <div class="honeypot" aria-hidden="true">
              <label>Website<input name="website" type="text" autocomplete="off" tabindex="-1" value="${escapeHtml(this.form.website)}" /></label>
            </div>
            ${this.checkboxField("ageAndResidenceConfirmed", "I confirm I am 18 or over and live in Victoria.")}
            <p class="submission-disclosure">By submitting, you agree to the giveaway terms and to receive your result or confirmation, the event offer and future Root to Rise emails. You can unsubscribe at any time.</p>
            <button type="submit" class="button button--primary submit-button" ${this.isSubmitting || upcoming || closed ? "disabled" : ""}>
              <span>${this.isSubmitting ? "Submitting…" : quizEntry ? "Reveal my result" : "Enter the draw"}</span>${this.isSubmitting ? "" : iconArrow()}
            </button>
            ${this.formErrors.website ? `<p class="submit-error" role="alert">${escapeHtml(this.formErrors.website)}</p>` : ""}
          </form>
          <nav class="legal-links" aria-label="Legal information">
            <a href="/terms.html" target="_blank" rel="noopener">Giveaway terms</a><span aria-hidden="true">·</span><a href="/privacy.html" target="_blank" rel="noopener">Privacy</a>
          </nav>
          ${quizEntry ? `<button type="button" class="text-button" data-action="back-to-quiz">${iconArrow("left")} Review my answers</button>` : `<button type="button" class="text-button" data-action="back-home">${iconArrow("left")} Back</button>`}
        </div>
      </section>`;
  }

  private inputField(
    name: "firstName" | "email",
    label: string,
    type: string,
    autocomplete: string,
    inputmode = "text",
    maxlength = 320,
  ): string {
    const error = this.formErrors[name];
    return `<div class="field${error ? " has-error" : ""}">
      <label for="${name}">${label}</label>
      <input id="${name}" name="${name}" type="${type}" autocomplete="${autocomplete}" inputmode="${inputmode}" maxlength="${maxlength}" value="${escapeHtml(this.form[name])}" aria-describedby="${error ? `${name}-error` : ""}" aria-invalid="${Boolean(error)}" placeholder="${label}" />
      ${error ? `<p class="field-error" id="${name}-error">${escapeHtml(error)}</p>` : ""}
    </div>`;
  }

  private checkboxField(name: "ageAndResidenceConfirmed", label: string): string {
    const error = this.formErrors[name];
    return `<div class="checkbox-wrap${error ? " has-error" : ""}">
      <label class="checkbox">
        <input name="${name}" type="checkbox" ${this.form[name] ? "checked" : ""} aria-describedby="${error ? `${name}-error` : ""}" />
        <span class="checkbox__box" aria-hidden="true"></span><span>${label}</span>
      </label>
      ${error ? `<p class="field-error" id="${name}-error">${escapeHtml(error)}</p>` : ""}
    </div>`;
  }

  private renderResult(): string {
    if (!this.score) return "";
    const result = quizConfig.results[this.score.terpene];
    if (!result) return "";
    const product = quizConfig.products[this.score.product];
    const standalone = this.standaloneMode;
    const closed = !standalone && !isGiveawayOpen() && !this.previewMode;
    const referralUrl = this.submission?.referralUrl ?? eventConfig.landingUrl;
    const entries = this.submission?.totalEntries ?? 0;
    const productImage = product.imageView === "both"
      ? "/assets/monday-sunday-calm-sleep.webp"
      : `/assets/monday-sunday-${product.imageView}.webp`;
    const productImageAlt = product.imageView === "both"
      ? "Monday–Sunday Calm and Sleep bottles"
      : `Monday–Sunday ${product.name} bottle`;

    return `
      <section class="result-screen screen-enter result--${result.id}">
        <div class="result-inner">
          ${this.logo()}
          <header class="result-header">
            <h1>You’re ${escapeHtml(result.name)}</h1>
            <p class="result-title">${escapeHtml(result.title)}</p>
          </header>
          <figure class="source-figure">
            <img src="${escapeHtml(result.sourceImage)}" alt="${escapeHtml(result.sourceLabel)}" width="1024" height="1024" />
            <figcaption>${escapeHtml(result.sourceLabel)}</figcaption>
          </figure>
          <div class="result-copy">
            ${result.personalityLines.map((line) => `<p>${escapeHtml(line)}</p>`).join("")}
            <p class="source-copy">${escapeHtml(result.description)}</p>
            <section class="found-in" aria-labelledby="found-in-heading">
              <h2 id="found-in-heading">Where you’ll find it</h2>
              <p>${result.foundIn.map(escapeHtml).join(" · ")}</p>
            </section>
          </div>
        </div>
        <section class="product-match">
          <div class="product-visual product-visual--${product.imageView}" aria-label="Monday–Sunday ${escapeHtml(product.name)} product">
            <img src="${productImage}" alt="${escapeHtml(productImageAlt)}" width="900" height="910" />
          </div>
          <div class="product-copy">
            <h2>Your match: <span>${escapeHtml(product.name)}</span></h2>
            <p>${escapeHtml(product.description)}</p>
          </div>
        </section>
        <div class="result-actions">
          <a class="button button--primary" href="${escapeHtml(product.affiliateUrl)}" target="_blank" rel="noopener noreferrer sponsored"><span>Shop ${escapeHtml(product.name)}</span>${iconArrow()}</a>
          ${standalone ? `<div class="compact-actions compact-actions--standalone">
              <button type="button" class="text-button" data-action="download">${iconDownload()} Download result card</button>
            </div>` : closed ? `<div class="closed-note"><strong>${escapeHtml(eventConfig.closedContent.heading)}</strong><span>${escapeHtml(eventConfig.closedContent.body)}</span></div>` : `
            <button type="button" class="button button--secondary" data-action="share">${iconShare()}<span>Share my result · earn up to ${eventConfig.referralLimit} extra entries</span></button>
            <div class="compact-actions">
              <button type="button" class="text-button" data-action="copy-link">Copy referral link</button>
              <button type="button" class="text-button" data-action="download">${iconDownload()} Download result card</button>
            </div>
            <p class="entry-total">Your current draw entries: <strong>${entries}</strong></p>`}
          <p class="action-notice" role="status">${escapeHtml(this.notice)}</p>
          <p class="medical-note">Personality quiz only—not a health assessment or medical advice.</p>
          <p class="product-note">Always read the label and follow the directions for use. If symptoms persist, talk to your health professional.</p>
          ${standalone ? `<nav class="result-return-links" aria-label="Quiz and website navigation">
            <button type="button" class="text-button retake" data-action="retake">Retake the quiz</button>
            <a class="text-button" href="https://www.roottorise.com.au/terpenes">Back to terpenes</a>
            <a class="text-button" href="https://www.roottorise.com.au/">Website home</a>
          </nav>` : `<button type="button" class="text-button retake" data-action="retake">Retake the quiz</button>`}
        </div>
        ${this.submission ? `<span class="sr-only">Personal referral link: ${escapeHtml(referralUrl)}</span>` : ""}
      </section>`;
  }

  private renderConfirmation(): string {
    const entries = this.submission?.totalEntries ?? 1;
    return `
      <section class="confirmation-screen screen-enter">
        <div class="confirmation-inner">
          ${this.logo()}
          <h1>You’re in the draw.</h1>
          <p>Your free entry for the ${escapeHtml(eventConfig.prize.title)} is confirmed. We’ll email your confirmation and the event offer.</p>
          <p class="confirmation-total">Current draw entries: <strong>${entries}</strong></p>
          <button type="button" class="button button--primary" data-action="start-quiz"><span>Discover my terpene</span>${iconArrow()}</button>
          <button type="button" class="button button--secondary" data-action="share-giveaway">${iconShare()}<span>Share the quiz · earn up to ${eventConfig.referralLimit} extra entries</span></button>
          <p class="action-notice" role="status">${escapeHtml(this.notice)}</p>
          <nav class="product-links" aria-label="Monday–Sunday products">
            <a href="${escapeHtml(quizConfig.products.calm.affiliateUrl)}" target="_blank" rel="noopener noreferrer sponsored">Explore Calm</a>
            <a href="${escapeHtml(quizConfig.products.sleep.affiliateUrl)}" target="_blank" rel="noopener noreferrer sponsored">Explore Sleep</a>
            <a href="${escapeHtml(quizConfig.products.both.affiliateUrl)}" target="_blank" rel="noopener noreferrer sponsored">Explore both</a>
          </nav>
          <p class="product-note">Always read the label and follow the directions for use. If symptoms persist, talk to your health professional.</p>
        </div>
      </section>`;
  }

  private bindEvents(): void {
    this.root.querySelector<HTMLElement>("[data-action='start-quiz']")?.addEventListener("click", () => this.startQuiz());
    this.root.querySelector<HTMLElement>("[data-action='start-giveaway']")?.addEventListener("click", () => this.startGiveaway());
    this.root.querySelector<HTMLElement>("[data-action='back']")?.addEventListener("click", () => this.previousQuestion());
    this.root.querySelector<HTMLElement>("[data-action='back-to-quiz']")?.addEventListener("click", () => {
      this.screen = "quiz";
      this.questionIndex = quizConfig.questions.length - 1;
      this.render();
    });
    this.root.querySelector<HTMLElement>("[data-action='back-home']")?.addEventListener("click", () => {
      this.screen = "landing";
      this.render();
    });
    this.root.querySelector<HTMLElement>("[data-action='retake']")?.addEventListener("click", () => this.startQuiz());
    this.root.querySelector<HTMLElement>("[data-action='close']")?.addEventListener("click", () => {
      window.parent.postMessage({ type: "r2r:close-quiz" }, "https://www.roottorise.com.au");
    });

    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-answer]")) {
      button.addEventListener("click", () => this.selectAnswer(button.dataset.answer ?? ""));
    }

    const form = this.root.querySelector<HTMLFormElement>(".entry-form");
    form?.addEventListener("input", (event) => this.updateFormValue(event));
    form?.addEventListener("change", (event) => this.updateFormValue(event));
    form?.addEventListener("submit", (event) => void this.handleSubmit(event));

    this.root.querySelector<HTMLElement>("[data-action='share']")?.addEventListener("click", () => void this.handleShare());
    this.root.querySelector<HTMLElement>("[data-action='copy-link']")?.addEventListener("click", () => void this.handleCopy());
    this.root.querySelector<HTMLElement>("[data-action='download']")?.addEventListener("click", () => void this.handleDownload());
    this.root.querySelector<HTMLElement>("[data-action='share-giveaway']")?.addEventListener("click", () => void this.handleGenericShare());
  }

  private startQuiz(): void {
    this.screen = "quiz";
    this.entryMethod = "quiz";
    this.questionIndex = 0;
    this.answers = {};
    this.score = null;
    this.submission = null;
    this.notice = "";
    this.render();
  }

  private startGiveaway(): void {
    this.screen = "entry";
    this.entryMethod = "giveaway";
    this.formErrors = {};
    this.notice = "";
    this.render();
  }

  private selectAnswer(answerId: string): void {
    const question = quizConfig.questions[this.questionIndex];
    if (!question || !question.answers.some((answer) => answer.id === answerId)) return;
    this.answers[question.id] = answerId;
    this.render();
    window.setTimeout(() => {
      if (this.questionIndex < quizConfig.questions.length - 1) {
        this.questionIndex += 1;
        this.render();
        return;
      }
      if (!hasCompleteQuiz(this.answers)) return;
      this.score = calculateQuizResult(this.answers);
      if (this.standaloneMode || (!isGiveawayOpen() && !isGiveawayUpcoming() && !this.previewMode)) {
        this.screen = "result";
      } else {
        this.screen = "entry";
        this.entryMethod = "quiz";
      }
      this.render();
    }, 240);
  }

  private previousQuestion(): void {
    if (this.questionIndex <= 0) return;
    this.questionIndex -= 1;
    this.render();
  }

  private updateFormValue(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const name = target.name as keyof EntryFormValues;
    if (name === "ageAndResidenceConfirmed") {
      this.form[name] = target.checked;
    } else if (name === "firstName" || name === "email" || name === "website") {
      this.form[name] = target.value;
    } else return;
    delete this.formErrors[name];
  }

  private campaignParameters(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "campaign"]) {
      const value = this.params.get(key);
      if (value) result[key] = value.slice(0, 100);
    }
    return result;
  }

  private async handleSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    this.formErrors = validateEntryForm(this.form);
    if (Object.keys(this.formErrors).length > 0) {
      this.notice = "Check the highlighted fields.";
      this.render();
      this.root.querySelector<HTMLElement>(".has-error input")?.focus();
      return;
    }

    if (this.entryMethod === "quiz" && !this.score) {
      this.notice = `Complete all ${quizConfig.questions.length} questions first.`;
      this.screen = "quiz";
      this.render();
      return;
    }

    const payload: SubmitEntryPayload = {
      eventId: this.previewMode ? `${eventConfig.id}-test` : eventConfig.id,
      testMode: this.previewMode,
      entryMethod: this.entryMethod,
      firstName: this.form.firstName.trim(),
      email: normalizeEmail(this.form.email),
      ageConfirmed: this.form.ageAndResidenceConfirmed,
      consentConfirmed: true,
      consentWordingVersion: eventConfig.consentWordingVersion,
      termsVersion: eventConfig.termsVersion,
      referralCode: this.params.get("ref")?.trim().toUpperCase() || undefined,
      campaign: this.campaignParameters(),
      website: this.form.website,
    };

    if (this.entryMethod === "quiz" && this.score) {
      payload.quizVersion = quizConfig.version;
      payload.quizAnswers = this.answers;
      payload.terpeneResult = this.score.terpene;
      payload.productMatch = this.score.product;
    }

    this.isSubmitting = true;
    this.notice = "Submitting your entry…";
    this.render();
    try {
      this.submission = await submitEntry(payload);
      this.notice = this.submission.status === "duplicate"
        ? "You already have a base entry. Your existing entry has been kept."
        : this.submission.status === "upgraded"
          ? "Your existing entry now includes your quiz result. No duplicate ticket was added."
          : "Your entry is confirmed.";
      if (this.entryMethod === "quiz" && this.submission.terpeneResult && this.submission.productMatch) {
        this.score = {
          terpene: this.submission.terpeneResult,
          product: this.submission.productMatch,
          totals: this.score?.totals ?? { myrcene: 0, caryophyllene: 0, limonene: 0, linalool: 0 },
        };
        this.screen = "result";
      } else {
        this.screen = "confirmation";
      }
    } catch (error) {
      this.formErrors.website = error instanceof Error ? error.message : "We could not submit your entry. Please try again.";
    } finally {
      this.isSubmitting = false;
      this.render();
    }
  }

  private async handleShare(): Promise<void> {
    if (!this.score || !this.submission) return;
    const result = quizConfig.results[this.score.terpene];
    if (!result) return;
    const outcome = await shareResult(result, this.submission.referralUrl);
    this.notice = outcome === "copied" ? "Your personalised referral link has been copied." : "Share ready.";
    this.render();
  }

  private async handleCopy(): Promise<void> {
    if (!this.submission) return;
    await copyReferralLink(this.submission.referralUrl);
    this.notice = "Your personalised referral link has been copied.";
    this.render();
  }

  private async handleDownload(): Promise<void> {
    if (!this.score) return;
    const result = quizConfig.results[this.score.terpene];
    if (!result) return;
    try {
      await downloadShareCard(result);
      this.notice = "Your result card download has started.";
    } catch {
      this.notice = "The result card could not be prepared. Please try again.";
    }
    this.render();
  }

  private async handleGenericShare(): Promise<void> {
    if (!this.submission) return;
    const shareData = {
      title: "Which terpene are you?",
      text: "Take the 45-second Root to Rise terpene personality quiz.",
      url: this.submission.referralUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        this.notice = "Share ready.";
        this.render();
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    await copyReferralLink(this.submission.referralUrl);
    this.notice = "Your personalised referral link has been copied.";
    this.render();
  }
}
